import type { EmbedReference } from '@shared/ast/astTypes'
import { isInlineEmbedReference } from '@shared/ast/embedReference'
import {
  $applyNodeReplacement,
  type DOMExportOutput,
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical'

import { InlineSourceEmbedView } from '../InlineSourceEmbedView'

export type SerializedCustomInlineEmbedNode = Spread<{ source: EmbedReference }, SerializedLexicalNode>

/** Atomic inline reference: fetched source text never enters the editor state. */
export class CustomInlineEmbedNode extends DecoratorNode<JSX.Element> {
  __source: EmbedReference

  static getType(): string {
    return 'custom-inline-embed'
  }

  static clone(node: CustomInlineEmbedNode): CustomInlineEmbedNode {
    return new CustomInlineEmbedNode(node.__source, node.__key)
  }

  constructor(source: EmbedReference, key?: NodeKey) {
    super(key)
    if (!isInlineEmbedReference(source)) {
      throw new Error('Inline embeds require a valid source block reference')
    }
    this.__source = { ...source }
  }

  getSource(): EmbedReference {
    return { ...this.getLatest().__source }
  }

  setSource(source: EmbedReference): this {
    if (!isInlineEmbedReference(source)) {
      throw new Error('Invalid inline embed reference')
    }
    this.getWritable().__source = { ...source }
    return this
  }

  createDOM(): HTMLElement {
    const element = document.createElement('span')
    element.setAttribute('data-inline-source-embed', 'true')
    element.contentEditable = 'false'
    return element
  }

  updateDOM(): false {
    return false
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span')
    element.setAttribute('data-inline-source-embed', JSON.stringify(this.__source))
    element.textContent = `↪ ${this.__source.filename}`
    return { element }
  }

  static importJSON(node: SerializedCustomInlineEmbedNode): CustomInlineEmbedNode {
    return $createCustomInlineEmbedNode(node.source)
  }

  exportJSON(): SerializedCustomInlineEmbedNode {
    return { type: 'custom-inline-embed', version: 1, source: { ...this.__source } }
  }

  isInline(): true {
    return true
  }

  isKeyboardSelectable(): false {
    return false
  }

  getTextContent(): string {
    return '\uFFFC'
  }

  decorate(): JSX.Element {
    return <InlineSourceEmbedView source={this.__source} nodeKey={this.__key} />
  }
}

export function $createCustomInlineEmbedNode(source: EmbedReference): CustomInlineEmbedNode {
  return $applyNodeReplacement(new CustomInlineEmbedNode(source))
}

export function $isCustomInlineEmbedNode(node: LexicalNode | null | undefined): node is CustomInlineEmbedNode {
  return node instanceof CustomInlineEmbedNode
}
